/*
 * Copy buttons. Two sources. A button inside a .code block copies that command,
 * minus the decorative `$` prompts and minus the lines marked data-output (what the
 * command printed, in a console block from rendered markdown). A button carrying data-copy copies that string
 * instead, with "." meaning this page's URL.
 *
 * A .code block marked data-copyable that arrives without a button (rendered
 * markdown) gets a clone of the layout's <template>, so the button's markup lives
 * in Blade and nowhere else. Opt-in, because the styleguide shows markup samples
 * that are not commands.
 */

const TEMPLATE_ID = 'grimoiry-copy-template';
const IDLE_LABEL = 'Copy to clipboard';
const RESET_AFTER_MS = 1400;

const resetTimers = new WeakMap<HTMLElement, number>();

function textToCopy(button: HTMLElement): string | null {
    const literal = button.dataset.copy;

    if (literal !== undefined) {
        return literal === '.' ? location.href.split('#')[0] : literal;
    }

    const source = button.closest('.code')?.querySelector('code');

    if (!source) {
        return null;
    }

    const hasOutput = source.querySelector('[data-output]') !== null;
    const text = [...source.childNodes]
        .filter((node) => !(node instanceof Element && node.matches('.prompt, [data-output]')))
        .map((node) => node.textContent ?? '')
        .join('')
        .replace(/^\n+|\n+$/g, '');

    // Taking the output away leaves the blank lines that stood around it.
    return hasOutput ? text.replace(/\n{2,}/g, '\n') : text;
}

function addMissingButtons(): void {
    const template = document.getElementById(TEMPLATE_ID);

    if (!(template instanceof HTMLTemplateElement)) {
        return;
    }

    document.querySelectorAll<HTMLElement>('.code[data-copyable]').forEach((block) => {
        if (!block.querySelector('.copy')) {
            block.append(template.content.cloneNode(true));
        }
    });
}

async function onClick(event: MouseEvent): Promise<void> {
    const button = (event.target as Element | null)?.closest<HTMLElement>('.copy, [data-copy]');

    if (!button) {
        return;
    }

    const text = textToCopy(button);

    if (!text) {
        return;
    }

    try {
        await navigator.clipboard.writeText(text);
    } catch {
        return; // clipboard blocked (insecure context, denied permission) — stay quiet
    }

    const idleLabel = button.dataset.idleLabel ?? IDLE_LABEL;

    button.dataset.state = 'copied';
    button.setAttribute('aria-label', 'Copied');
    window.clearTimeout(resetTimers.get(button));
    resetTimers.set(
        button,
        window.setTimeout(() => {
            button.dataset.state = 'idle';
            button.setAttribute('aria-label', idleLabel);
        }, RESET_AFTER_MS),
    );
}

export function initCopyButtons(): void {
    addMissingButtons();
    document.addEventListener('click', (event) => void onClick(event));
}
